'use client';

import { createGlobalStyle } from 'styled-components';

import { visuallyHiddenStyles } from '@lib/styles';

import { darkHomeTokens } from './tokens';

/**
 * Three tiers here, and which tier a rule lands in is the part people get
 * wrong.
 *
 * `body` is UNLAYERED on purpose. The rule it has to beat — DVD's own `body`
 * block in src/dvd/tokens/global-styles.ts — sits above that sheet's
 * `@layer site-base` and is itself unlayered, and an unlayered rule beats a
 * layered one outright, before specificity is consulted. Both are `body` at
 * 0,0,1, so the tie falls to injection order: this sheet mounts in the surface
 * layout, below the root provider, and lands last. `background` is the
 * shorthand deliberately — DVD paints four radial-gradient layers on `body`,
 * and `background-color` alone would leave them underneath.
 *
 * Element defaults sit in `@layer dark-home-base`, which beats the layered
 * `site-base` by order and loses to every unlayered surface component — the
 * "above that sheet, below my own components" slot only layers can express.
 * Layer order follows first occurrence, so the order statement below appends
 * dark-home-base after site-base with no central edit, and also makes this
 * sheet correct standalone in Storybook.
 *
 * Utilities stay UNLAYERED because they are hard overrides that must win
 * wherever they land: a layered `.dark-home-skip-link` could be pulled back
 * into view by any styled component, which is why DVD keeps its own
 * `.visually-hidden` out of `site-base` too. See rule 2 of
 * docs/adrs/20260807-blog-css-cascade-layers.md.
 *
 * Contract: docs/adrs/20260813-creative-surfaces.md ("Layers").
 */
export const DarkHomeGlobalStyles = createGlobalStyle`
  @layer site-base, dark-home-base;

  /* The canvas colour. The colour scheme is not here — it is on SurfaceRoot
     (./styled), which wraps only what paints on the canvas. */
  body {
    background: ${({ theme }) => theme.colors.blue800};
  }

  /*
   * The order statement above reserves this surface's slot between site-base
   * and its own components, and there are deliberately no rules in it yet.
   *
   * Every heading and paragraph the surface renders is a styled component
   * that declares its own margin, so a blanket heading/paragraph margin reset
   * would do nothing for the surface — and createGlobalStyle is document-wide,
   * so it would reach the shared Footer, whose "Your complete video marketing
   * platform" tagline is a bare <p> that does rely on the site-base margin.
   * The one thing such a reset would change is the one thing it should not.
   *
   * Add element defaults here when the surface actually grows author-supplied
   * markup it does not style itself. Layer order follows first occurrence, so
   * they will land in the right slot with no central edit.
   */

  .dark-home-skip-link {
    ${visuallyHiddenStyles}

    &:focus-visible {
      background-color: ${({ theme }) => theme.colors.blue800};
      border: 1px solid ${darkHomeTokens.colors.hairline};
      border-radius: ${({ theme }) => theme.spacing[0.5]};
      clip: auto;
      clip-path: none;
      color: ${darkHomeTokens.colors.bodyLight};
      height: auto;
      left: ${({ theme }) => theme.spacing[1]};
      padding: ${({ theme }) => theme.spacing[0.75]}
        ${({ theme }) => theme.spacing[1]};
      position: fixed;
      top: ${({ theme }) => theme.spacing[1]};
      width: auto;
      /* Above the shared Navigation's sticky bar, which sits at 999. */
      z-index: 1000;
    }
  }
`;
